import { toQuery, type HttpClient } from '../client';
import type { GovSummaryRow } from '../types/analytics';
import type {
  GovCategoryMetricRow,
  GovDeptRow,
  GovDrillQuery,
  GovDrillResult,
  GovGroupResult,
  GovPerformanceResult,
  GovQuery,
  GovRankResult,
  GovRefreshState,
  GovReportResult,
  GovSkuMetricRow,
  GovStructureItem,
  GovTopicResult
} from '../types/gov';

/** 监管驾驶舱（/api/gov/*）：全部读聚合快照，权限码 wl:analytics:view。 */
export function createGovApi(http: HttpClient) {
  return {
    /** 全区总览。 */
    overview(query: GovQuery = {}): Promise<GovSummaryRow> {
      return http.request<GovSummaryRow>({ path: '/gov/overview', query: toQuery(query) });
    },
    /**
     * 手动刷新监管聚合快照（不必等 Hq.Worker 整点任务）。
     * 权限码 `sys:config:manage`；`periodType` 缺省 AUTO = 同时刷新 DAY + MONTH。
     */
    refresh(query: { periodType?: string; statDate?: string; withDimensions?: boolean } = {}): Promise<{
      statDate: string;
      periods: {
        periodType: string;
        statDate: string;
        orgCount: number;
        groupRows: number;
        dimensionRows: number;
        refreshedAt: string;
      }[];
    }> {
      return http.request({ path: '/gov/refresh', method: 'POST', query: toQuery(query) });
    },
    /** 机构排行（含同比/环比/排名变化）。 */
    rank(query: GovQuery = {}): Promise<GovRankResult> {
      return http.request<GovRankResult>({ path: '/gov/rank', query: toQuery(query) });
    },
    /** 层级结构（卫健局 → 机构）。 */
    structure(query: GovQuery = {}): Promise<{ periodType: string; statDate: string; items: GovStructureItem[] }> {
      return http.request({ path: '/gov/structure', query: toQuery(query) });
    },
    /** 绩效对比（四指标同屏）。 */
    performance(query: GovQuery = {}): Promise<GovPerformanceResult> {
      return http.request<GovPerformanceResult>({ path: '/gov/performance', query: toQuery(query) });
    },
    /** 五级穿透：ORG / DEPT / CATEGORY / SKU。 */
    drill<T = unknown>(query: GovDrillQuery = {}): Promise<GovDrillResult<T>> {
      return http.request<GovDrillResult<T>>({ path: '/gov/drill', query: toQuery(query) });
    },
    /** 科室层下钻。 */
    deptDrill(query: GovQuery = {}): Promise<GovDrillResult<GovDeptRow>> {
      return this.drill<GovDeptRow>({ ...query, level: 'DEPT' });
    },
    /** 品类层下钻。 */
    categoryDrill(query: GovQuery = {}): Promise<GovDrillResult<GovCategoryMetricRow>> {
      return this.drill<GovCategoryMetricRow>({ ...query, level: 'CATEGORY' });
    },
    /** 单品层下钻。 */
    skuDrill(query: GovQuery = {}): Promise<GovDrillResult<GovSkuMetricRow>> {
      return this.drill<GovSkuMetricRow>({ ...query, level: 'SKU' });
    },
    /** 专题分析（预算/应急/异常/满意度）。 */
    topic(query: GovQuery = {}): Promise<GovTopicResult> {
      return http.request<GovTopicResult>({ path: '/gov/topic', query: toQuery(query) });
    },
    /** 周期报告（本期/上期/去年同期 + 数据截至时间）。 */
    report(query: GovQuery = {}): Promise<GovReportResult> {
      return http.request<GovReportResult>({ path: '/gov/report', query: toQuery(query) });
    },
    /**
     * 聚合刷新状态（轻量探针）：只返回 refreshAt / statDate / stale 等字段。
     * 大屏用它判断「是否有新一轮聚合」，有变化才拉全量，避免加大取数频率。
     */
    refreshState(query: GovQuery = {}): Promise<GovRefreshState> {
      return http.request<GovRefreshState>({ path: '/gov/refresh-state', query: toQuery(query) });
    },
    /** 主院/医共体视角。 */
    group(query: GovQuery = {}): Promise<GovGroupResult> {
      return http.request<GovGroupResult>({ path: '/gov/group', query: toQuery(query) });
    }
  };
}

export type GovApi = ReturnType<typeof createGovApi>;
